import { GraphQLError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GenericFormScene } from 'shared-ui/lib/components/generic-form-scene/GenericFormScene';
import { parseGraphqlErrors } from 'shared-ui/lib/utils/get-gql-error';
import { routesConfig } from '../../config/routes.config';
import { groupForm, GroupFormBody } from '../../form/group/group.form';
import {
  useCreateGroupMutation,
  useSetGroupUsersMutation,
  useSetGroupVehiclesMutation,
} from '../../generated/graphql';

export const GroupCreateScene = (): JSX.Element => {
  const { t } = useTranslation('GroupCreateScene');
  const navigate = useNavigate();
  const [createGroup, { loading: isCreateLoading }] = useCreateGroupMutation();
  const [setGroupVehicles, { loading: isVehiclesLoading }] = useSetGroupVehiclesMutation();
  const [setGroupUsers, { loading: isUsersLoading }] = useSetGroupUsersMutation();

  const handleSubmit = async (body: GroupFormBody) => {
    try {
      const { data, errors } = await createGroup({
        variables: {
          body: {
            name: body.name,
            ...(body.notificationEmail ? { notificationEmail: body.notificationEmail } : {}),
          },
        },
      });
      if (!data) throw new Error(parseGraphqlErrors(errors as GraphQLError[]));

      const id = data.createGroup.id;
      const vehicleIds = body.vehicleIds ?? [];
      const userIds = body.userIds ?? [];

      if (vehicleIds.length) {
        await setGroupVehicles({ variables: { id, body: { vehicleIds } } });
      }
      if (userIds.length) {
        await setGroupUsers({ variables: { id, body: { userIds } } });
      }

      toast.success(t('General:updated'));
      navigate(routesConfig.SETTINGS.GROUPS.INDEX);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <GenericFormScene
      type="create"
      title={t('createGroup')}
      onSubmit={handleSubmit}
      form={groupForm}
      isSubmitLoading={isCreateLoading || isVehiclesLoading || isUsersLoading}
      backLink={routesConfig.SETTINGS.GROUPS.INDEX}
      onSubmitValidationFailed={() => toast.error(t('notSavedCheckFields'))}
    />
  );
};
